import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import NuAmount from "../../../../../Components/Utils/NuAmount";
import axiosInstance from "../../../../../Services/axiosCommon";
import { useNavigate } from "react-router-dom";

const MonthWiseSales = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const [params, setParams] = useState({
    year: currentYear,
  });
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  async function getData() {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/new-dashboard/sales-order/chart`, { params });
      setData(res?.data || []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  useEffect(() => {
    getData();
  }, [params]);

  const [salesData, setSalesData] = useState([]);

  const years = Array.from(
    new Array(10),
    (val, index) => currentYear - 5 + index
  );

  useEffect(() => {
    if (data && data.data) {
      const formattedData = data.data.map((item) => ({
        month: new Date(0, item.month - 1).toLocaleString("default", {
          month: "short",
        }),
        amount: item.price || 0,
        monthIndex: item.month - 1,
      }));
      setSalesData(formattedData);
    }
  }, [data]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-900 text-white shadow-xl rounded-xl p-3 text-xs border border-gray-700">
          <p className="font-semibold text-gray-300 mb-1">{`Month: ${payload[0].payload.month}`}</p>
          <p className="text-emerald-400 font-bold">
            Sales: <NuAmount value={payload[0].value} />
          </p>
        </div>
      );
    }
    return null;
  };

  const handleYearChange = (e) => {
    const newYear = e.target.value;
    setParams({ year: newYear });
    setSelectedYear(newYear);
  };

  const handleSliceClick = (monthIndex) => {
    const year = selectedYear;
    const startDate = new Date(year, monthIndex, 1).toISOString().split("T")[0];
    const endDate = new Date(year, monthIndex + 1, 0).toISOString().split("T")[0];

    const path = "/sales/sales-orders";
    navigate(path, {
      state: {
        fromDate: startDate,
        toDate: endDate,
        month: monthIndex + 1,
      },
    });
  };

  return (
    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-[0_4px_25px_rgba(0,0,0,0.04)] transition-all duration-300">
      <div className="flex justify-between items-center pb-3 border-b border-gray-100 mb-4">
        <div className="flex items-center space-x-2">
          <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-800">Monthly Sales Orders Trend</h3>
        </div>

        <select
          value={selectedYear}
          onChange={handleYearChange}
          className="bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1 text-xs font-medium text-gray-700 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="h-64 flex items-center justify-center animate-pulse">
          <div className="h-40 bg-gray-100 rounded-xl w-full"></div>
        </div>
      ) : (
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={salesData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: "#6B7280" }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: "#6B7280" }}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(243, 244, 246, 0.6)" }} />
              <Bar
                dataKey="amount"
                radius={[6, 6, 0, 0]}
                barSize={20}
                onClick={(data) => handleSliceClick(data.monthIndex)}
                style={{ cursor: "pointer" }}
              >
                {salesData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill="#3B82F6"
                    className="hover:opacity-85 transition-opacity"
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default MonthWiseSales;
